import { CSSProperties } from "react"
import { css, cx } from "@emotion/css"
import { scaleLinear } from "@visx/scale"

const styles = {
  wrapper: css`
    width: 100%;
    position: relative;
    height: var(--h);
    display: grid;
    overflow: hidden;
  `,
  track: css`
    grid-area: 1 / -1;
    width: 100%;
    height: 100%;
    border-radius: var(--r);
    background-color: var(--track-color, #e7e6e6);
  `,
  bar: css`
    grid-area: 1 / -1;
    display: flex;
    position: relative;
    overflow: hidden;
    width: var(--w);
    height: var(--h);
    border-radius: var(--r);
    background-color: var(--track-color, #e7e6e6);
  `,
  part: css`
    height: 100%;
    &:first-of-type {
      border-top-left-radius: var(--r);
      border-bottom-left-radius: var(--r);
    }
    &:last-of-type {
      border-top-right-radius: var(--r);
      border-bottom-right-radius: var(--r);
    }
  `,
}

export type StackBarProps = {
  width: number // 0 - 100
  //
  thickness?: number
  radius?: number
  trackColor?: string
  //
  values: Record<string, number>
  getColor: (key: string, idx: number) => string
}

export function StackBar(props: StackBarProps) {
  const { values, width, thickness = 6, radius, trackColor, getColor } = props
  const w = Number(width.toFixed(2))
  const h = thickness
  const r = radius ?? h / 2

  const max = Math.max(...Object.values(values))
  const scale = scaleLinear<number>({
    domain: [0, max],
    range: [0, 100],
    nice: true,
    round: true,
  })

  const style = {
    "--w": `${w}%`,
    "--h": `${h}px`,
    "--r": `${r}px`,
    "--track-color": trackColor || "var(--light-grey, #e7e6e6)",
  } as CSSProperties

  const parts = Object.entries(values).map(([key, value]) => {
    return { key, value, width: scale(value) }
  })

  parts.sort((a, b) => b.value - a.value)

  return (
    <div style={style} className={styles.wrapper}>
      <div data-track className={styles.track} />
      <div className={styles.bar}>
        {parts.map((part, index) => {
          return (
            <div
              key={index}
              className={cx(styles.part, "StackBarPart")}
              style={{
                backgroundColor: getColor(part.key, index),
                width: `clamp(2px, ${part.width}%, 100%)`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
